import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, CardContent, Chip, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { PrintOutlined, GridOnOutlined, LocalPrintshopOutlined } from '@mui/icons-material';
import { PageContainer, PageCard, SectionHeader } from '../../common/components/styled-ui-elements';
import { BackToDashboardButton } from '../../common/components/navigation';
import { PrintPopupPortal } from '../../common/components/PrintPopupPortal';
// Direct file imports, not the `sections` barrel — that barrel also re-exports components
// (MaintenanceScheduleSection, AssetCostSummarySection) that pull in AssetService's RTK Query
// api, which hits a pre-existing ESM-parsing issue under this CRA/Jest setup (same reason
// AssetLabel.test.tsx/LabelSheet.test.tsx import LabelSheet directly rather than via that barrel).
import { LabelSheet, TOTAL_CELLS } from '../components/AssetForm/sections/LabelSheet';
import type { LabelAsset } from '../components/AssetForm/sections/AssetLabel';

interface PrintAssetLabelsLocationState {
  assets?: LabelAsset[];
}

interface PrintRequest {
  popupWindow: Window;
  outlineOnly: boolean;
}

function clampStartPosition(value: number): number {
  if (Number.isNaN(value)) return 1;
  return Math.min(Math.max(value, 1), TOTAL_CELLS);
}

interface SelectedAssetsListProps {
  assets: LabelAsset[];
}

// Extracted so the selected-assets summary list doesn't add to the page's own complexity.
function SelectedAssetsList({ assets }: SelectedAssetsListProps) {
  return (
    <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
      {assets.map((asset) => (
        <ListItem key={asset.id}>
          <ListItemText primary={asset.name} secondary={asset.assetTag} />
        </ListItem>
      ))}
    </List>
  );
}

// Opens a fresh popup tab and hands it to `PrintPopupPortal` (T129's isolated-printing fix —
// see that component's doc comment). Must call `window.open` synchronously inside the caller's
// own click handler, not inside an effect, or browsers' popup blockers can reject it.
function openPrintRequest(outlineOnly: boolean): PrintRequest | null {
  const popupWindow = window.open('', '_blank');
  if (!popupWindow) return null;
  popupWindow.document.title = 'Trackly Asset Labels';
  popupWindow.document.body.style.margin = '0';
  return { popupWindow, outlineOnly };
}

/**
 * PrintAssetLabels (T129): bulk label-sheet printing for OL1735LP pre-cut label sheets — the
 * selected assets (from `AssetList`'s bulk checkbox selection, or a single asset pre-seeded from
 * `AssetEdit`'s "Print Label" action), a start-position field so a partially used sheet isn't
 * reprinted over (FR-015), an on-screen `LabelSheet` preview, a "Print sheet" action, and a
 * "Print alignment test" action that prints the same grid as outlines only on plain paper, for
 * verifying registration before committing a real label sheet (research.md item 8).
 *
 * Both print actions render into a separate popup tab via `PrintPopupPortal`, rather than calling
 * `window.print()` on this page directly — isolates a known Chromium tab-crash bug (browser back
 * button clicked during the print-preview dialog) to a disposable tab instead of this one. The
 * on-screen preview below always shows the real labels; only the popup's copy switches to
 * outline-only for the alignment test.
 */
const PrintAssetLabels: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const assets = (location.state as PrintAssetLabelsLocationState | null)?.assets ?? [];
  const [startPosition, setStartPosition] = useState(1);
  const [printRequest, setPrintRequest] = useState<PrintRequest | null>(null);

  const printSheet = () => setPrintRequest(openPrintRequest(false));
  const printAlignmentTest = () => setPrintRequest(openPrintRequest(true));
  const closePrintPopup = () => setPrintRequest(null);

  if (assets.length === 0) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                No assets were selected for printing. Go back to the asset list and select one or more assets.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/assets')}>
                Back to Assets
              </Button>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <Box sx={{ backgroundColor: '#ffffff', borderBottom: '2px solid #3b82f6', p: 2.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <SectionHeader icon={<PrintOutlined sx={{ mr: 1, color: '#d97706' }} />} title="Print Asset Labels" />
              <BackToDashboardButton />
            </Box>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Chip label={`${assets.length} asset${assets.length !== 1 ? 's' : ''} selected`} sx={{ mb: 2 }} />
            <SelectedAssetsList assets={assets} />

            <TextField
              label="Start position"
              type="number"
              size="small"
              value={startPosition}
              onChange={(e) => setStartPosition(clampStartPosition(parseInt(e.target.value, 10)))}
              helperText={`1-${TOTAL_CELLS} — labels before this position print blank`}
              sx={{ mt: 2, mb: 2, maxWidth: 220 }}
              slotProps={{ htmlInput: { min: 1, max: TOTAL_CELLS } }}
            />

            <Box display="flex" gap={2}>
              <Button variant="contained" startIcon={<LocalPrintshopOutlined />} onClick={printSheet}>
                Print sheet
              </Button>
              <Button variant="outlined" startIcon={<GridOnOutlined />} onClick={printAlignmentTest}>
                Print alignment test
              </Button>
            </Box>
          </CardContent>

          <Box sx={{ overflow: 'auto', p: 2 }}>
            <Box className="print-preview-zoom" sx={{ transform: 'scale(0.55)', transformOrigin: 'top left', width: '8.5in' }}>
              <LabelSheet assets={assets} startPosition={startPosition} outlineOnly={false} />
            </Box>
          </Box>
        </PageCard>
      </Box>

      {printRequest && (
        <PrintPopupPortal popupWindow={printRequest.popupWindow} onClose={closePrintPopup}>
          <LabelSheet assets={assets} startPosition={startPosition} outlineOnly={printRequest.outlineOnly} />
        </PrintPopupPortal>
      )}
    </PageContainer>
  );
};


export default PrintAssetLabels;
